import React from 'react'
import { Card, Button, Space, Typography, InputNumber, Switch, Slider, Divider } from 'antd'
import { ExpandOutlined, ShrinkOutlined, BorderOutlined } from '@ant-design/icons'
import { useDesignStore } from '../../store/designStore'

const { Title, Text } = Typography

const CanvasControlsPanel: React.FC = () => {
  const { canvasSettings, updateCanvasSettings } = useDesignStore()

  const handleSizeChange = (dimension: 'width' | 'height', value: number) => {
    updateCanvasSettings({ [dimension]: value })
  }

  const handleGridToggle = (checked: boolean) => {
    updateCanvasSettings({ showGrid: checked })
  }

  const handleGridSizeChange = (value: number) => {
    updateCanvasSettings({ gridSize: value })
  }

  const presetSizes = [
    { name: 'Small Yard', width: 30, height: 30 },
    { name: 'Medium Yard', width: 50, height: 50 },
    { name: 'Large Yard', width: 80, height: 80 },
    { name: 'Estate', width: 120, height: 120 }
  ]

  return (
    <Card
      title={
        <Space>
          <ExpandOutlined />
          <Text strong>Canvas Settings</Text>
        </Space>
      }
      style={{ width: 350, zIndex: 100 }}
      bodyStyle={{ padding: '15px' }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Canvas Size */}
        <div>
          <Title level={5}>Canvas Size</Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text>Width: {canvasSettings.width} ft</Text>
              <Slider
                min={20}
                max={200}
                step={5}
                value={canvasSettings.width}
                onChange={(value) => handleSizeChange('width', value)}
                style={{ marginTop: '8px' }}
              />
            </div>
            <div>
              <Text>Height: {canvasSettings.height} ft</Text>
              <Slider
                min={20}
                max={200}
                step={5}
                value={canvasSettings.height}
                onChange={(value) => handleSizeChange('height', value)}
                style={{ marginTop: '8px' }}
              />
            </div>
          </Space>
        </div>

        <Divider />

        {/* Quick Presets */}
        <div>
          <Title level={5}>Quick Presets</Title>
          <Space wrap>
            {presetSizes.map((preset) => (
              <Button
                key={preset.name}
                size="small"
                onClick={() => updateCanvasSettings({ 
                  width: preset.width, 
                  height: preset.height 
                })}
                type={
                  canvasSettings.width === preset.width && 
                  canvasSettings.height === preset.height 
                    ? 'primary' 
                    : 'default'
                }
              >
                {preset.name}
              </Button>
            ))}
          </Space>
        </div>

        <Divider />

        {/* Grid Settings */}
        <div>
          <Title level={5}>Grid Settings</Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text>Show Grid:</Text>
              <Switch
                checked={canvasSettings.showGrid}
                onChange={handleGridToggle}
                checkedChildren={<BorderOutlined />}
                unCheckedChildren="Off"
              />
            </div>
            
            {canvasSettings.showGrid && (
              <div>
                <Text>Grid Size: {canvasSettings.gridSize} ft</Text>
                <Slider
                  min={1}
                  max={10}
                  step={0.5}
                  value={canvasSettings.gridSize}
                  onChange={handleGridSizeChange}
                  style={{ marginTop: '8px' }}
                />
              </div>
            )}
          </Space>
        </div>

        <Divider />

        {/* Current Info */}
        <div>
          <Title level={5}>Current Canvas</Title>
          <Space direction="vertical">
            <Text type="secondary">
              📐 Size: {canvasSettings.width} × {canvasSettings.height} ft
            </Text>
            <Text type="secondary">
              📊 Area: {(canvasSettings.width * canvasSettings.height).toLocaleString()} sq ft
            </Text>
            <Text type="secondary">
              🏠 Acres: {((canvasSettings.width * canvasSettings.height) / 43560).toFixed(2)}
            </Text>
          </Space>
        </div>
      </Space>
    </Card>
  )
}

export default CanvasControlsPanel
